import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";

export default function DeleteAccountRow() {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const doDelete = async (e) => {
    e.preventDefault();
    if (confirmText.trim().toUpperCase() !== "DELETE" || busy) return;
    setBusy(true);
    try {
      await api.post("/users/me/delete", { confirm: "DELETE" });
      setUser(false);
      toast.success("Your account has been permanently deleted.");
      navigate("/");
    } catch (err) {
      toast.error(extractError(err) || "Deletion failed. Please try again.");
      setBusy(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          data-testid="profile-menu-delete-account"
          className="w-full flex items-center gap-4 p-4 rounded-2xl transition-colors border border-red-900/40 bg-red-950/10 hover:bg-red-950/30 text-left"
        >
          <div className="w-11 h-11 rounded-full grid place-items-center shrink-0 bg-red-900/60">
            <Trash2 size={20} className="text-red-300" strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-red-300 font-medium leading-tight">Delete account</div>
            <div className="text-[11px] text-zinc-500 leading-tight mt-0.5">Permanently erase your account and data</div>
          </div>
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="delete-account-dialog" className="bg-zinc-950 border-zinc-800">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-red-400">Delete your account forever?</AlertDialogTitle>
          <AlertDialogDescription>
            This is permanent and cannot be undone. Your profile, posts, followers,
            messages access, and remaining coin balance will be erased. Type
            <span className="font-bold text-red-400"> DELETE </span> to confirm.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Input
          data-testid="delete-account-confirm-input"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="Type DELETE"
          autoComplete="off"
          className="bg-black border-zinc-700"
        />
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-account-cancel-btn" onClick={() => setConfirmText("")}>
            Keep my account
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="delete-account-confirm-btn"
            disabled={confirmText.trim().toUpperCase() !== "DELETE" || busy}
            onClick={doDelete}
            className="bg-red-600 hover:bg-red-500 text-white disabled:opacity-40"
          >
            {busy ? "Deleting…" : "Delete forever"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
